import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, PackageCheck, Truck, CalendarClock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Reveal, Eyebrow } from "./Reveal";

const STATUS_ICON = {
  Delivered: PackageCheck,
  "In Transit": Truck,
  Scheduled: CalendarClock,
};

export default function TrackWidget() {
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const track = async (e) => {
    e.preventDefault();
    if (!ref.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/track/${ref.trim()}`);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Not found");
    } finally {
      setLoading(false);
    }
  };

  const Icon = result ? STATUS_ICON[result.status] || Truck : Truck;

  return (
    <section id="track" className="relative py-28 lg:py-36 bg-ink-2" data-testid="track">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
        <Reveal><Eyebrow className="justify-center">Live Tracking</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl">
            Track your <span className="italic text-gold">shipment.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-5 text-cream/60 font-light">
            Enter your reference number (e.g. <span className="text-gold">PT-A1B2C3</span>) to see live status.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <form onSubmit={track} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value.toUpperCase())}
              placeholder="Enter reference number"
              data-testid="track-input"
              className="flex-1 bg-ink border border-gold/20 px-5 py-4 text-cream tracking-wider placeholder:text-cream/30 focus:outline-none focus:ring-2 focus:ring-gold/60"
            />
            <button
              type="submit"
              data-testid="track-submit"
              disabled={loading}
              className="btn-gold border border-gold text-gold px-8 py-4 tracking-widest text-sm flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              TRACK
            </button>
          </form>
        </Reveal>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              data-testid="track-error"
              className="mt-6 text-cream/70 border border-destructive/40 bg-destructive/10 py-3 px-4 max-w-xl mx-auto"
            >
              {error}
            </motion.div>
          )}
          {result && (
            <motion.div
              key={result.reference}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              data-testid="track-result"
              className="mt-10 text-left bg-ink border border-gold/25 p-8 max-w-xl mx-auto"
            >
              <div className="flex items-center justify-between">
                <span className="font-serif text-2xl text-gold">{result.reference}</span>
                <span className="flex items-center gap-2 text-sm tracking-widest uppercase text-cream">
                  <Icon size={18} className="text-gold" /> {result.status}
                </span>
              </div>
              <div className="mt-6 space-y-3 text-sm">
                <Row label="Service" value={result.service} />
                <Row label="Origin" value={result.origin} />
                <Row label="Destination" value={result.destination} />
                <Row label="ETA" value={result.eta} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-gold/10 pb-2">
      <span className="text-cream/50">{label}</span>
      <span className="text-cream">{value}</span>
    </div>
  );
}
